import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, ChevronLeft, CreditCard, MessageSquare, Shield, Zap, Phone, MessageCircle, Video, Bot, Headphones, Users, BarChart3, Lightbulb, ArrowUp, Link, X } from 'lucide-react';
import { SITE_URL } from '../lib/config';

type OnboardingStep = 'terms' | 'phone' | 'phone-verify' | 'plan' | 'payment' | 'intro' | 'name' | 'goals' | 'connect' | 'suggestions';

export function OnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || 'user@example.com';

  const [step, setStep] = useState<OnboardingStep>('terms');
  const [agreed, setAgreed] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [selectedPlan, setSelectedPlan] = useState<string>('');

  // Payment form state
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'google'>('card');
  const [fullName, setFullName] = useState('');
  const [country, setCountry] = useState('United States');
  const [address, setAddress] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [differentInvoiceName, setDifferentInvoiceName] = useState(false);
  const [invoiceName, setInvoiceName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Name and goals state
  const [displayName, setDisplayName] = useState('');
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);

  const handleTermsContinue = () => {
    if (agreed) {
      setStep('phone');
    }
  };

  const handleSendCode = () => {
    if (phone.trim()) {
      setStep('phone-verify');
    }
  };

  const handleVerifyPhone = () => {
    if (phoneCode.trim()) {
      setStep('plan');
    }
  };

  const handleSelectPlan = (plan: string) => {
    setSelectedPlan(plan);
    setStep('payment');
  };

  const handleSubscribe = () => {
    // TODO: Implement payment processing
    console.log('Subscribing to:', selectedPlan, 'billing:', billingCycle);
    setStep('intro');
  };

  const handleGetStarted = () => {
    // Extract first name from email as default display name
    const emailName = email.split('@')[0];
    const firstName = emailName.charAt(0).toUpperCase() + emailName.slice(1).split(/[._-]/)[0];
    setDisplayName(firstName);
    setStep('name');
  };

  const handleNameContinue = () => {
    if (displayName.trim()) {
      setStep('goals');
    }
  };

  const toggleGoal = (goal: string) => {
    setSelectedGoals(prev =>
      prev.includes(goal)
        ? prev.filter(g => g !== goal)
        : prev.length < 3 ? [...prev, goal] : prev
    );
  };

  const handleGoalsContinue = () => {
    // TODO: Save user preferences
    console.log('User:', displayName, 'Goals:', selectedGoals);
    setStep('connect');
  };

  const handleConnectZoom = () => {
    // TODO: Implement Zoom OAuth flow
    console.log('Initiating Zoom connection...');
    // For now, simulate successful connection and go to suggestions
    setStep('suggestions');
  };

  const handleSkipConnect = () => {
    // Continue to suggestions in trial/explore mode without Zoom connection
    setStep('suggestions');
  };

  const handleSuggestionClick = (suggestion: string) => {
    // Navigate to chat with the selected suggestion as initial prompt
    navigate('/chat', { state: { initialPrompt: suggestion } });
  };

  const handleStartFresh = () => {
    // Navigate to chat without a pre-filled prompt
    navigate('/chat');
  };

  const getPlanPrice = (plan: string) => {
    const prices: Record<string, { base: number; perSeat: number }> = {
      starter: { base: 50, perSeat: 1 },
      basic: { base: 199, perSeat: 1 },
      pro: { base: 999, perSeat: 2 },
    };
    return prices[plan] || { base: 0, perSeat: 0 };
  };

  const getDiscountedPrice = (base: number) => {
    return billingCycle === 'annual' ? Math.round(base * 0.8) : base;
  };

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    const groups = digits.match(/.{1,4}/g) || [];
    return groups.join(' ').slice(0, 19);
  };

  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 2) {
      return digits.slice(0, 2) + '/' + digits.slice(2, 4);
    }
    return digits;
  };

  const handleResendCode = () => {
    // TODO: Implement resend
    console.log('Resending code to', phone);
  };

  const handleUseDifferentEmail = () => {
    navigate('/');
  };

  const formatPhone = (value: string) => {
    // Remove non-digits
    const digits = value.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header - just logo */}
      <header className="py-6 flex justify-center">
        <div className="flex items-center gap-2">
          <video
            src="/zac-avatar.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-8 h-8 rounded-lg object-cover"
          />
          <span className="text-xl font-semibold">Ask <span className="text-primary">Zac</span></span>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-8">
        <div className={`w-full text-center ${step === 'plan' ? 'max-w-4xl' : 'max-w-md'}`}>

          {/* Step 1: Terms */}
          {step === 'terms' && (
            <>
              <h1 className="text-3xl font-semibold mb-2">Let's create your account</h1>
              <p className="text-muted-foreground mb-8">A few things for you to review</p>

              <div className="bg-card border border-border rounded-xl p-6">
                <label className="flex items-start gap-3 text-left cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-border accent-primary"
                  />
                  <span className="text-sm text-muted-foreground">
                    I agree to Ask Zac's{' '}
                    <a href="/terms" className="text-primary hover:underline">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>
                    {' '}and confirm that I am at least 18 years of age.
                  </span>
                </label>

                <button
                  onClick={handleTermsContinue}
                  disabled={!agreed}
                  className={`w-full mt-6 px-6 py-3 rounded-lg font-medium transition-colors ${
                    agreed
                      ? 'bg-white hover:bg-gray-100 text-gray-900'
                      : 'bg-secondary text-muted-foreground cursor-not-allowed'
                  }`}
                >
                  Continue
                </button>
              </div>
            </>
          )}

          {/* Step 2: Phone Entry */}
          {step === 'phone' && (
            <>
              <h1 className="text-3xl font-semibold mb-2">Verify your phone number</h1>
              <p className="text-muted-foreground mb-8">Enter your phone number to get a verification code</p>

              <div className="space-y-4">
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none">
                    <span className="text-lg">🇺🇸</span>
                  </div>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    placeholder="(555) 867-5309"
                    className="w-full bg-input border border-border rounded-lg pl-12 pr-4 py-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <button
                  onClick={handleSendCode}
                  disabled={phone.replace(/\D/g, '').length < 10}
                  className={`w-full px-6 py-3 rounded-lg font-medium transition-colors ${
                    phone.replace(/\D/g, '').length >= 10
                      ? 'bg-white hover:bg-gray-100 text-gray-900'
                      : 'bg-secondary text-muted-foreground cursor-not-allowed'
                  }`}
                >
                  Send verification code
                </button>

                <p className="text-muted-foreground text-sm">
                  SMS and data charges may apply.{' '}
                  <a href="#" className="text-primary hover:underline">Learn more</a>
                </p>
              </div>
            </>
          )}

          {/* Step 3: Phone Verification */}
          {step === 'phone-verify' && (
            <>
              <h1 className="text-3xl font-semibold mb-2">Verify your phone number</h1>
              <p className="text-muted-foreground mb-8">Enter the code sent via text to: {phone}</p>

              <div className="space-y-4">
                <input
                  type="text"
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter code"
                  className="w-full bg-input border border-border rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-center text-lg tracking-widest"
                  maxLength={6}
                />

                <button
                  onClick={handleVerifyPhone}
                  disabled={phoneCode.length < 6}
                  className={`w-full px-6 py-3 rounded-lg font-medium transition-colors ${
                    phoneCode.length >= 6
                      ? 'bg-white hover:bg-gray-100 text-gray-900'
                      : 'bg-secondary text-muted-foreground cursor-not-allowed'
                  }`}
                >
                  Verify code
                </button>

                <p className="text-muted-foreground text-sm">
                  Not seeing the code?{' '}
                  <button onClick={handleResendCode} className="text-primary hover:underline">
                    Try again
                  </button>
                </p>
              </div>
            </>
          )}

          {/* Step 4: Plan Selection */}
          {step === 'plan' && (
            <div className="max-w-5xl">
              <h1 className="text-3xl font-semibold mb-4">Choose your plan</h1>
              <p className="text-muted-foreground mb-8">Base price + per-seat. Scales with your Zoom investment.</p>

              {/* Billing Toggle */}
              <div className="flex items-center justify-center gap-3 mb-8">
                <span className={`text-sm ${billingCycle === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>Monthly</span>
                <button
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                  className={`relative w-12 h-6 rounded-full transition-colors ${billingCycle === 'annual' ? 'bg-primary' : 'bg-secondary'}`}
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${billingCycle === 'annual' ? 'left-7' : 'left-1'}`} />
                </button>
                <span className={`text-sm ${billingCycle === 'annual' ? 'text-foreground' : 'text-muted-foreground'}`}>Annual</span>
                {billingCycle === 'annual' && <span className="text-xs text-green-400 font-medium">Save 20%</span>}
              </div>

              {/* Plan Cards */}
              <div className="grid md:grid-cols-4 gap-4 text-left">
                {/* Starter */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-1">Starter</h3>
                  <p className="text-muted-foreground text-sm mb-4">Discover & Learn</p>
                  <div className="mb-1">
                    <span className="text-2xl font-bold">$50</span>
                    <span className="text-muted-foreground text-sm">/mo</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">+ $1/seat</p>
                  <button
                    onClick={() => handleSelectPlan('starter')}
                    className="w-full bg-secondary hover:bg-secondary/80 text-foreground px-4 py-2.5 rounded-lg font-medium transition-colors border border-border text-sm mb-4"
                  >
                    Get Started
                  </button>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      All Zoom products
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Natural language Q&A
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Adoption score & health check
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Unused feature detection
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Weekly scans
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      2 admin users
                    </li>
                  </ul>
                </div>

                {/* Basic */}
                <div className="bg-card border-2 border-primary rounded-xl p-6 relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                  <h3 className="text-lg font-semibold mb-1">Basic</h3>
                  <p className="text-muted-foreground text-sm mb-4">Adopt & Configure</p>
                  <div className="mb-1">
                    <span className="text-2xl font-bold">$199</span>
                    <span className="text-muted-foreground text-sm">/mo</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">+ $1/seat</p>
                  <button
                    onClick={() => handleSelectPlan('basic')}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2.5 rounded-lg font-medium transition-colors text-sm mb-4"
                  >
                    Start 14-Day Trial
                  </button>
                  <p className="text-xs text-muted-foreground mb-2">Everything in Starter, plus:</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Natural language configuration
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      User-level adoption data
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Personalized end-user emails
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Real-time scans
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      5 admins + 10 viewers
                    </li>
                  </ul>
                </div>

                {/* Pro */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-1">Pro</h3>
                  <p className="text-muted-foreground text-sm mb-4">Verify & Prove ROI</p>
                  <div className="mb-1">
                    <span className="text-2xl font-bold">$999</span>
                    <span className="text-muted-foreground text-sm">/mo</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">+ $2/seat</p>
                  <button
                    onClick={() => handleSelectPlan('pro')}
                    className="w-full bg-secondary hover:bg-secondary/80 text-foreground px-4 py-2.5 rounded-lg font-medium transition-colors border border-border text-sm mb-4"
                  >
                    Start 14-Day Trial
                  </button>
                  <p className="text-xs text-muted-foreground mb-2">Everything in Basic, plus:</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Sandbox accounts
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Virtual caller testing
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      ROI & executive reports
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Custom dashboards
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Unlimited users
                    </li>
                  </ul>
                </div>

                {/* Enterprise */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-1">Enterprise</h3>
                  <p className="text-muted-foreground text-sm mb-4">Partnership</p>
                  <div className="mb-1">
                    <span className="text-2xl font-bold">Custom</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">Volume discounts</p>
                  <button
                    onClick={() => window.location.href = 'mailto:sales@askzac.ai'}
                    className="w-full bg-secondary hover:bg-secondary/80 text-foreground px-4 py-2.5 rounded-lg font-medium transition-colors border border-border text-sm mb-4"
                  >
                    Contact Sales
                  </button>
                  <p className="text-xs text-muted-foreground mb-2">Everything in Pro, plus:</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      SSO (SAML/OIDC)
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      SCIM provisioning
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Full API access
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Dedicated CSM
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      99.95% SLA
                    </li>
                  </ul>
                </div>
              </div>

              <p className="text-muted-foreground text-xs mt-6 text-center">
                Prices shown are base prices before per-seat fees. Annual plans are 20% off. <a href={`${SITE_URL}/pricing`} className="text-primary hover:underline">View full pricing</a>
              </p>
            </div>
          )}

          {/* Step 5: Payment */}
          {step === 'payment' && (
            <div className="max-w-md text-left">
              {/* Back button and plan name */}
              <button
                onClick={() => setStep('plan')}
                className="flex items-center gap-1 text-muted-foreground hover:text-foreground mb-6 text-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>

              <h1 className="text-2xl font-semibold mb-6 capitalize">{selectedPlan}</h1>

              {/* Billing cycle selection */}
              <div className="space-y-3 mb-6">
                <label className="flex items-center gap-3 p-3 bg-card border border-border rounded-lg cursor-pointer">
                  <input
                    type="radio"
                    name="billing"
                    checked={billingCycle === 'annual'}
                    onChange={() => setBillingCycle('annual')}
                    className="w-4 h-4 accent-primary"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Yearly</span>
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Save 20%</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      ${getDiscountedPrice(getPlanPrice(selectedPlan).base)}/mo base + ${getPlanPrice(selectedPlan).perSeat}/seat
                    </p>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 bg-card border border-border rounded-lg cursor-pointer">
                  <input
                    type="radio"
                    name="billing"
                    checked={billingCycle === 'monthly'}
                    onChange={() => setBillingCycle('monthly')}
                    className="w-4 h-4 accent-primary"
                  />
                  <div className="flex-1">
                    <span className="font-medium">Monthly</span>
                    <p className="text-sm text-muted-foreground">
                      ${getPlanPrice(selectedPlan).base}/mo base + ${getPlanPrice(selectedPlan).perSeat}/seat
                    </p>
                  </div>
                </label>
              </div>

              {/* Order details */}
              <div className="bg-card border border-border rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium mb-3">Order details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground capitalize">{selectedPlan} plan (base)</span>
                    <span>${getDiscountedPrice(getPlanPrice(selectedPlan).base)}/mo</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Per-seat fee</span>
                    <span>+ ${getPlanPrice(selectedPlan).perSeat}/seat</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Billed {billingCycle === 'annual' ? 'yearly' : 'monthly'}</span>
                    <span></span>
                  </div>
                  <div className="border-t border-border pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Base subtotal</span>
                      <span>${billingCycle === 'annual' ? getDiscountedPrice(getPlanPrice(selectedPlan).base) * 12 : getDiscountedPrice(getPlanPrice(selectedPlan).base)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between font-medium pt-1">
                    <span>Due today (base only)</span>
                    <span>${billingCycle === 'annual' ? getDiscountedPrice(getPlanPrice(selectedPlan).base) * 12 : getDiscountedPrice(getPlanPrice(selectedPlan).base)}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Per-seat charges will be calculated after connecting your Zoom account. Plan auto-renews {billingCycle === 'annual' ? 'annually' : 'monthly'} until canceled.
                </p>
              </div>

              {/* Payment method */}
              <div className="mb-6">
                <h3 className="text-sm font-medium mb-3">Payment method</h3>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-muted-foreground">Full name</label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Name on card"
                      className="w-full mt-1 bg-input border border-border rounded-lg px-4 py-2.5 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-muted-foreground">Country</label>
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="w-full mt-1 bg-input border border-border rounded-lg px-4 py-2.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option>United States</option>
                      <option>Canada</option>
                      <option>United Kingdom</option>
                      <option>Australia</option>
                      <option>Germany</option>
                      <option>France</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm text-muted-foreground">Address</label>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Street address"
                      className="w-full mt-1 bg-input border border-border rounded-lg px-4 py-2.5 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  {/* Payment method tabs */}
                  <div className="flex gap-2 border-b border-border">
                    <button
                      onClick={() => setPaymentMethod('card')}
                      className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-colors ${
                        paymentMethod === 'card'
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <CreditCard className="w-4 h-4" />
                      Card
                    </button>
                    <button
                      onClick={() => setPaymentMethod('google')}
                      className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-colors ${
                        paymentMethod === 'google'
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      </svg>
                      Google Pay
                    </button>
                  </div>

                  {paymentMethod === 'card' && (
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-muted-foreground">Card number</label>
                        <input
                          type="text"
                          value={cardNumber}
                          onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                          placeholder="1234 5678 9012 3456"
                          className="w-full mt-1 bg-input border border-border rounded-lg px-4 py-2.5 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          maxLength={19}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-muted-foreground">Expiration date</label>
                          <input
                            type="text"
                            value={expiry}
                            onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                            placeholder="MM/YY"
                            className="w-full mt-1 bg-input border border-border rounded-lg px-4 py-2.5 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            maxLength={5}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground">Security code (CVC)</label>
                          <input
                            type="text"
                            value={cvc}
                            onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            placeholder="123"
                            className="w-full mt-1 bg-input border border-border rounded-lg px-4 py-2.5 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            maxLength={4}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {paymentMethod === 'google' && (
                    <div className="text-center py-8 text-muted-foreground">
                      <p className="text-sm">Click Subscribe to pay with Google Pay</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Invoice name checkbox */}
              <label className="flex items-start gap-3 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={differentInvoiceName}
                  onChange={(e) => setDifferentInvoiceName(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-border accent-primary"
                />
                <span className="text-sm text-muted-foreground">Use a different name on invoices</span>
              </label>

              {differentInvoiceName && (
                <input
                  type="text"
                  value={invoiceName}
                  onChange={(e) => setInvoiceName(e.target.value)}
                  placeholder="Invoice name"
                  className="w-full mb-4 bg-input border border-border rounded-lg px-4 py-2.5 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              )}

              {/* Authorize charges */}
              <label className="flex items-start gap-3 mb-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-border accent-primary"
                />
                <span className="text-sm text-muted-foreground">
                  I authorize Ask Zac to charge my payment method for the amount shown above, plus applicable per-seat fees based on my Zoom seat count. I understand my subscription will auto-renew until canceled.
                </span>
              </label>

              {/* Subscribe button */}
              <button
                onClick={handleSubscribe}
                disabled={!agreedToTerms || (paymentMethod === 'card' && (!fullName || !cardNumber || !expiry || !cvc))}
                className={`w-full px-6 py-3 rounded-lg font-medium transition-colors ${
                  agreedToTerms && (paymentMethod === 'google' || (fullName && cardNumber && expiry && cvc))
                    ? 'bg-white hover:bg-gray-100 text-gray-900'
                    : 'bg-secondary text-muted-foreground cursor-not-allowed'
                }`}
              >
                Subscribe
              </button>
            </div>
          )}

          {/* Step 6: Introduction */}
          {step === 'intro' && (
            <div className="max-w-lg text-left">
              {/* Zac avatar */}
              <video
                src="/zac-avatar.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-12 h-12 rounded-xl object-cover mb-6"
              />

              <h1 className="text-3xl font-semibold mb-4">Hey there, I'm Zac.</h1>
              <p className="text-muted-foreground mb-2">
                I'm your AI assistant for discovering, adopting, and configuring Zoom products.
              </p>
              <p className="text-muted-foreground mb-8">
                Here's a few things you should know about me:
              </p>

              <div className="space-y-6 mb-8">
                {/* Feature 1 */}
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Ask me anything about Zoom</h3>
                    <p className="text-sm text-muted-foreground">
                      Chat with me about your Zoom setup, features you're not using, or how to configure any product. I'm always ready to help.
                    </p>
                  </div>
                </div>

                {/* Feature 2 */}
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">I'll always ask before making changes</h3>
                    <p className="text-sm text-muted-foreground">
                      I can help configure your Zoom account, but I'll never make changes without your explicit approval. You're always in control.
                    </p>
                  </div>
                </div>

                {/* Feature 3 */}
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Connect your Zoom account to get started</h3>
                    <p className="text-sm text-muted-foreground">
                      Once connected, I can analyze your current setup, find unused features, and help you get more value from your Zoom investment.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleGetStarted}
                className="bg-white hover:bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Got it
              </button>
            </div>
          )}

          {/* Step 7: Name Confirmation */}
          {step === 'name' && (
            <div className="max-w-lg text-left">
              {/* Zac avatar */}
              <video
                src="/zac-avatar.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-12 h-12 rounded-xl object-cover mb-6"
              />

              <p className="text-lg mb-6">
                Is <span className="font-medium">{displayName}</span> how I should refer to you? If not, you can change that now.
              </p>

              <div className="relative mb-6">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="text-primary text-xs font-medium">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleNameContinue()}
                  className="w-full bg-input border border-border rounded-lg pl-14 pr-12 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={handleNameContinue}
                  disabled={!displayName.trim()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-muted-foreground/20 hover:bg-muted-foreground/30 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 8: Goals Selection */}
          {step === 'goals' && (
            <div className="max-w-lg text-left">
              {/* Zac avatar */}
              <video
                src="/zac-avatar.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-12 h-12 rounded-xl object-cover mb-6"
              />

              <p className="text-lg mb-6">
                What are you focused on, {displayName}? Pick up to three topics to explore.
              </p>

              <div className="flex flex-wrap gap-2 mb-8">
                {[
                  { id: 'contact-center', label: 'Contact Center', icon: Headphones },
                  { id: 'phone', label: 'Phone System', icon: Phone },
                  { id: 'meetings', label: 'Meetings & Webinars', icon: Video },
                  { id: 'team-chat', label: 'Team Chat', icon: MessageCircle },
                  { id: 'ai-companion', label: 'AI Companion', icon: Bot },
                  { id: 'quality-mgmt', label: 'Quality Management', icon: BarChart3 },
                  { id: 'workforce', label: 'Workforce Management', icon: Users },
                  { id: 'zac-choice', label: "Zac's recommendation", icon: Lightbulb },
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => toggleGoal(id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors text-sm ${
                      selectedGoals.includes(id)
                        ? 'bg-primary/20 border-primary text-foreground'
                        : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>

              <button
                onClick={handleGoalsContinue}
                disabled={selectedGoals.length === 0}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  selectedGoals.length > 0
                    ? 'bg-white hover:bg-gray-100 text-gray-900'
                    : 'bg-secondary text-muted-foreground cursor-not-allowed'
                }`}
              >
                Let's go
              </button>
            </div>
          )}

          {/* Step 9: Connect Zoom Account */}
          {step === 'connect' && (
            <div className="max-w-lg text-left">
              {/* Zac avatar */}
              <video
                src="/zac-avatar.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-12 h-12 rounded-xl object-cover mb-6"
              />

              <p className="text-lg mb-2">
                All set, {displayName}! Here's a few ideas just for you.
              </p>
              <p className="text-muted-foreground mb-8">
                Where should we start?
              </p>

              {/* Suggestion cards - dimmed in background */}
              <div className="space-y-3 mb-8 opacity-50">
                <div className="flex items-center gap-3 p-4 bg-card border border-border rounded-lg">
                  <BarChart3 className="w-5 h-5 text-muted-foreground" />
                  <span className="text-muted-foreground">Show me my adoption score</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-card border border-border rounded-lg">
                  <Lightbulb className="w-5 h-5 text-muted-foreground" />
                  <span className="text-muted-foreground">Find features I'm not using</span>
                </div>
              </div>

              {/* Connect modal overlay */}
              <div className="relative">
                <div className="bg-card border border-border rounded-xl p-6 shadow-2xl">
                  <button
                    onClick={handleSkipConnect}
                    className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  <div className="flex items-center gap-2 mb-4">
                    <Link className="w-5 h-5 text-primary" />
                    <span className="text-xs text-primary font-medium uppercase tracking-wide">Connect</span>
                  </div>

                  <h3 className="text-xl font-semibold mb-2">
                    Connect your Zoom account to unlock the full experience.
                  </h3>

                  <ul className="space-y-2 mb-6 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      See your actual configuration and adoption data
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Get personalized recommendations for your setup
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      Configure Zoom products with natural language
                    </li>
                  </ul>

                  <div className="space-y-3">
                    <button
                      onClick={handleConnectZoom}
                      className="w-full bg-white hover:bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <rect width="24" height="24" rx="4" fill="#2D8CFF"/>
                        <path d="M6 9.5C6 8.67 6.67 8 7.5 8H12.5C13.33 8 14 8.67 14 9.5V14.5C14 15.33 13.33 16 12.5 16H7.5C6.67 16 6 15.33 6 14.5V9.5Z" fill="white"/>
                        <path d="M14.5 10.5L17.5 8.5V15.5L14.5 13.5V10.5Z" fill="white"/>
                      </svg>
                      Connect Zoom Account
                    </button>
                    <button
                      onClick={handleSkipConnect}
                      className="w-full text-muted-foreground hover:text-foreground text-sm transition-colors"
                    >
                      Maybe later — explore in trial mode
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 10: Suggestions */}
          {step === 'suggestions' && (
            <div className="max-w-lg text-left">
              {/* Zac avatar */}
              <video
                src="/zac-avatar.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-12 h-12 rounded-xl object-cover mb-6"
              />

              <p className="text-lg mb-2">
                Great! Where should we start, {displayName}?
              </p>
              <p className="text-muted-foreground mb-8">
                Here are some popular questions to get you going:
              </p>

              {/* Suggestion cards */}
              <div className="space-y-3 mb-8">
                <button
                  onClick={() => handleSuggestionClick("Show me who is using which licenses in my Zoom account")}
                  className="w-full flex items-center gap-3 p-4 bg-card border border-border rounded-lg hover:border-primary/50 hover:bg-card/80 transition-colors text-left group"
                >
                  <Users className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="text-muted-foreground group-hover:text-foreground transition-colors">Show me who is using which licenses</span>
                </button>

                <button
                  onClick={() => handleSuggestionClick("Create an adoption report for my Zoom deployment")}
                  className="w-full flex items-center gap-3 p-4 bg-card border border-border rounded-lg hover:border-primary/50 hover:bg-card/80 transition-colors text-left group"
                >
                  <BarChart3 className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="text-muted-foreground group-hover:text-foreground transition-colors">Create an adoption report</span>
                </button>

                <button
                  onClick={() => handleSuggestionClick("What Zoom features am I not using that I should explore?")}
                  className="w-full flex items-center gap-3 p-4 bg-card border border-border rounded-lg hover:border-primary/50 hover:bg-card/80 transition-colors text-left group"
                >
                  <Lightbulb className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="text-muted-foreground group-hover:text-foreground transition-colors">Suggest areas of Zoom I should explore</span>
                </button>

                <button
                  onClick={() => handleSuggestionClick("What's my current Zoom health score and how can I improve it?")}
                  className="w-full flex items-center gap-3 p-4 bg-card border border-border rounded-lg hover:border-primary/50 hover:bg-card/80 transition-colors text-left group"
                >
                  <Zap className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="text-muted-foreground group-hover:text-foreground transition-colors">Check my Zoom health score</span>
                </button>

                <button
                  onClick={() => handleSuggestionClick("Help me understand my Contact Center configuration")}
                  className="w-full flex items-center gap-3 p-4 bg-card border border-border rounded-lg hover:border-primary/50 hover:bg-card/80 transition-colors text-left group"
                >
                  <Headphones className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="text-muted-foreground group-hover:text-foreground transition-colors">Understand my Contact Center setup</span>
                </button>
              </div>

              <button
                onClick={handleStartFresh}
                className="text-muted-foreground hover:text-foreground text-sm transition-colors"
              >
                Or start with my own question →
              </button>
            </div>
          )}

        </div>
      </main>

      {/* Footer - hide on plan selection, payment, intro, name, goals, connect, and suggestions */}
      {step !== 'plan' && step !== 'payment' && step !== 'intro' && step !== 'name' && step !== 'goals' && step !== 'connect' && step !== 'suggestions' && (
        <footer className="py-8 text-center">
          <p className="text-muted-foreground text-sm">
            Email verified as <span className="text-foreground">{email}</span>
          </p>
          <button
            onClick={handleUseDifferentEmail}
            className="text-primary hover:underline text-sm mt-1"
          >
            Use a different email
          </button>
        </footer>
      )}
    </div>
  );
}
